import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import "@/index.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { SettingsProvider } from "@/context/SettingsContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Library from "@/pages/Library";
import Program from "@/pages/Program";
import ContentDetail from "@/pages/ContentDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Sponsors from "@/pages/Sponsors";
import Account from "@/pages/Account";
import Live from "@/pages/Live";
import Admin from "@/pages/Admin";
import AdminHelp from "@/pages/AdminHelp";
import Privacy from "@/pages/Privacy";
import SplashLoader from "@/components/SplashLoader";
import InstallAppBanner from "@/components/InstallAppBanner";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-slate-500">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  const location = useLocation();
  const hideFooter = location.pathname === "/login";
  return (
    <>
      <Navbar />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Home />} />
          <Route path="/program" element={<Program />} />
          <Route path="/supported-by" element={<Sponsors />} />
          <Route path="/library/:category" element={<ProtectedRoute><Library /></ProtectedRoute>} />
          <Route path="/library" element={<Navigate to="/library/video" replace />} />
          <Route path="/content/:id" element={<ContentDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/live" element={<ProtectedRoute><Live /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
          <Route path="/admin/help" element={<ProtectedRoute adminOnly><AdminHelp /></ProtectedRoute>} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
      {!hideFooter && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-ink-900 font-body text-slate-100">
      <SplashLoader />
      <InstallAppBanner />
      <div className="noise-overlay" />
      <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
        <LanguageProvider>
          <AuthProvider>
            <SettingsProvider>
              <BrowserRouter>
                <Shell />
              </BrowserRouter>
              <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#0A0F1A", border: "1px solid rgba(0,240,255,0.2)", color: "#F8FAFC", fontFamily: "JetBrains Mono, monospace", fontSize: "12px" } }} />
            </SettingsProvider>
          </AuthProvider>
        </LanguageProvider>
      </ReactLenis>
    </div>
  );
}

export default App;
